import { toNumber } from '../utils/numberUtils.js'
import { toYearMonth, monthDiff, fechaToPeriodo, periodoToDate } from '../utils/dateUtils.js'
import { obtenerPBUEnFecha, PBU_VIGENTE, HABER_MINIMO_LEGAL } from './engine/tipos.js'
import { calcularHaberMixto } from './engine/formulas/formulaGeneral.js'
import { calcularHaberDocente } from './engine/formulas/formulaDocente.js'
import { calcularHaberPension } from './engine/formulas/formulaPension.js'
import { buildMovilidadDesdeCese, getRipdocFactor } from './updates/mobilityEscalator.js'
import seriesOficiales from './data/seriesOficiales.json'

function diasComputablesRow(r = {}) {
  const candidates = [r?.diasComputables, r?.diasOrigen, r?.dias]
  for (const candidate of candidates) {
    const n = Number(candidate)
    if (Number.isFinite(n) && Number.isInteger(n) && n > 0) return n
  }
  return 30
}

function toPeriodoDisplay(value) {
  const txt = String(value || '')
  const slash = txt.match(/^(\d{2})\/(\d{4})$/)
  if (slash) return `${slash[1]}/${slash[2]}`
  const iso = txt.match(/^(\d{4})-(\d{2})/)
  if (iso) return `${iso[2]}/${iso[1]}`
  return txt || '-'
}

function minusMonthsIso(dateValue, months) {
  const d = new Date(dateValue)
  if (Number.isNaN(d.getTime())) return ''
  d.setMonth(d.getMonth() - Number(months || 0))
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function computeEdadEnFecha(fechaNacimiento, fechaCorte) {
  const nac = new Date(fechaNacimiento)
  const corte = new Date(fechaCorte)
  if (Number.isNaN(nac.getTime()) || Number.isNaN(corte.getTime())) return 0
  let edad = corte.getFullYear() - nac.getFullYear()
  const m = corte.getMonth() - nac.getMonth()
  if (m < 0 || (m === 0 && corte.getDate() < nac.getDate())) edad -= 1
  return Math.max(0, edad)
}


export default function composeDictamenContext(rows = [], expediente = {}, options = {}) {
  const data = Array.isArray(rows) ? rows : []
  const mode = String(options?.mode || 'avanzado')
  const normativeVersion = String(options?.normativeVersion || 'N/D')
  const confiscatoriedadThresholdPct = Number(options?.confiscatoriedadThresholdPct || 0.15) * 100
  const isBaseMode = mode === 'base'
  const valid = data.filter((r) => !r.invalidoPorCese)
  const conTope = valid.filter((r) => String(r.fuenteNormativa) === 'tope_base_imponible')
  const rowsConQuita = valid.map((r) => {
    const montoOriginal = toNumber(r.montoOriginal)
    const montoLegal = toNumber(r.montoSujetoAAporte)
    const quitaPct = montoOriginal > 0 ? ((montoOriginal - montoLegal) / montoOriginal) * 100 : 0
    return {
      periodo: r.periodoKey || r.periodo || r.periodoISO || '-',
      montoOriginal,
      montoSujetoAAporte: montoLegal,
      montoActualizado: toNumber(r.montoActualizado || montoLegal),
      quitaPct,
    }
  })

  const ventanaPromedio = valid
    .filter((r) => r.incluirEnPromedio !== false)
    .slice()
    .sort((a, b) => toYearMonth(a.periodoKey || a.periodo || a.periodoISO) - toYearMonth(b.periodoKey || b.periodo || b.periodoISO))
    .slice(-120)

  const esAutonomoRow = (r) => {
    const reg = String(r?.regimenContributivo || '').toUpperCase()
    if (reg === 'AUTONOMO') return true
    if (reg === 'DEPENDENCIA') return false
    const tag = String(r?.tipoActividad || r?.tipo || r?.tipoRaw || '').toLowerCase()
    return tag.includes('autonom') || tag.includes('mono')
  }
  const ventanaDependencia = ventanaPromedio.filter((r) => !esAutonomoRow(r))
  const ventanaAutonomos = ventanaPromedio.filter((r) => esAutonomoRow(r))

  const haberAnses = ventanaPromedio.reduce((acc, r) => acc + toNumber(r.montoOriginal), 0)
  const haberReajustado = ventanaPromedio.reduce(
    (acc, r) => acc + toNumber(r.montoActualizadoPromedio || r.montoActualizado || r.montoBasePromedio || r.montoSujetoAAporte),
    0,
  )
  const divisorPromedio = Math.max(1, ventanaPromedio.length)
  const diferencial = haberAnses > 0 ? ((haberReajustado - haberAnses) / haberAnses) * 100 : 0
  const divisorPolicy = String(options?.divisorPolicy || 'legal_120')
  const divisorLegal = 120
  const mesesConAportes = Math.max(1, ventanaPromedio.length)
  const divisorEfectivo = mesesConAportes < divisorLegal ? mesesConAportes : divisorLegal
  const divisorAplicado = divisorPolicy === 'muestra_efectiva' ? mesesConAportes : divisorEfectivo
  const promedio120 = haberReajustado / divisorAplicado
  const promedioDependencia = ventanaDependencia.length
    ? ventanaDependencia.reduce((acc, r) => acc + toNumber(r.montoActualizado || r.montoSujetoAAporte), 0) /
      Math.max(1, ventanaDependencia.length)
    : 0
  const promedioAutonomos = ventanaAutonomos.length
    ? ventanaAutonomos.reduce((acc, r) => acc + toNumber(r.montoActualizado || r.montoSujetoAAporte), 0) /
      Math.max(1, ventanaAutonomos.length)
    : 0

  let peor = { periodo: '-', quita: 0 }
  valid.forEach((r) => {
    const base = toNumber(r.montoOriginal)
    const legal = toNumber(r.montoSujetoAAporte)
    if (base <= 0) return
    const quita = ((base - legal) / base) * 100
    if (quita > peor.quita) peor = { periodo: r.periodoKey || r.periodo || '-', quita }
  })

  const alertas = []
  if (peor.quita > confiscatoriedadThresholdPct) alertas.push(`Superacion del umbral de confiscatoriedad del ${confiscatoriedadThresholdPct.toFixed(2)}% (Doctrina CSJN)`) 
  const gaps = valid.filter((r) => r.requiereRevision).length
  if (gaps > 0) alertas.push(`Gaps de aportes detectados: ${gaps} meses en revision`)
  const mono = valid.filter((r) => String(r.fuenteNormativa) === 'renta_monotributo').length
  if (mono > 0) alertas.push(`Deteccion de ${mono} meses con monotributo normalizado por renta de referencia`)
  if (ventanaDependencia.length > 0 && ventanaAutonomos.length > 0) {
    alertas.push('Caso mixto detectado: se aplico separacion de haberes (dependencia/autonomos) para control anti-contaminacion del promedio.')
  }

  const topRemuneracionesActualizadas = {
    top5RemuneracionesActualizadas: rowsConQuita
      .slice()
      .sort((a, b) => b.montoActualizado - a.montoActualizado)
      .slice(0, 5),
    top5MayoresQuitasPorTope: rowsConQuita
      .filter((r) => r.quitaPct > 0)
      .slice()
      .sort((a, b) => b.quitaPct - a.quitaPct)
      .slice(0, 5),
  }

  const periodoDesde = ventanaPromedio[0]?.periodoKey || ventanaPromedio[0]?.periodo || ventanaPromedio[0]?.periodoISO || '-'
  const periodoHasta = ventanaPromedio[ventanaPromedio.length - 1]?.periodoKey || ventanaPromedio[ventanaPromedio.length - 1]?.periodo || ventanaPromedio[ventanaPromedio.length - 1]?.periodoISO || '-'
  const periodoComputoPromedio = `Periodo de computo del promedio: desde ${toPeriodoDisplay(periodoDesde)} hasta ${toPeriodoDisplay(periodoHasta)}`
  const sexo = String(expediente?.sexo || 'F').toUpperCase()
  const hijos = toNumber(expediente?.cantidadHijos)
  const adoptivos = toNumber(expediente?.hijosAdoptivos)
  const discapacidad = toNumber(expediente?.hijosDiscapacidad)
  const bonusPorHijos = sexo === 'F' || sexo === 'X' ? (hijos * 1 + adoptivos * 2 + discapacidad * 1 + (expediente?.cobraAUH === 'si' ? 1 : 0)) : 0

  const aporteDiasBase = valid
    .filter((r) => r.incluirEnPromedio !== false)
    .reduce((acc, r) => acc + diasComputablesRow(r), 0)
  const aporteMesesEquivalentes = aporteDiasBase / 30
  const aporteAniosBase = aporteDiasBase / 365
  const aporteAniosTotal = aporteAniosBase + bonusPorHijos
  const mesesFaltantesMoratoria = aporteAniosTotal >= 30 ? 0 : Math.ceil((30 - aporteAniosTotal) * 12)
  const mostrarTareasCuidado = bonusPorHijos > 0
  const narrativaViabilidad = aporteAniosTotal < 30
    ? `La viabilidad jubilatoria requiere combinar aportes efectivos (${aporteAniosBase.toFixed(2)} años, ${aporteMesesEquivalentes.toFixed(2)} meses equivalentes) con regularización por moratoria Ley 27.705 (${mesesFaltantesMoratoria} meses)${mostrarTareasCuidado ? ` y tareas de cuidado (${bonusPorHijos.toFixed(2)} años)` : ''}.`
    : 'La viabilidad jubilatoria surge de aportes efectivos y servicios reconocidos sin necesidad de regularización adicional.'

  const ventanaDesde = ventanaPromedio[0]?.periodoKey || ventanaPromedio[0]?.periodo || ventanaPromedio[0]?.periodoISO || '-'
  const ventanaHasta = ventanaPromedio[ventanaPromedio.length - 1]?.periodoKey || ventanaPromedio[ventanaPromedio.length - 1]?.periodo || ventanaPromedio[ventanaPromedio.length - 1]?.periodoISO || '-'
  const promedioFundamento = ventanaPromedio.length >= 120
    ? 'Se han tomado las últimas 120 remuneraciones con aportes de forma cronológica inversa, cumpliendo taxativamente con el Art. 24 de la Ley 24.241, garantizando la no arbitrariedad en la conformación de la base de cálculo.'
    : `Se han tomado ${ventanaPromedio.length} remuneraciones con aportes disponibles. El divisor se contrae al total de meses efectivos (${divisorEfectivo}) conforme Art. 24 Ley 24.241 para evitar licuar el promedio.`

  const hojaDeTrabajo = {
    numerador: Number(haberReajustado.toFixed(2)),
    divisor: divisorAplicado,
    fundamento: promedioFundamento,
    periodoDesde: ventanaDesde,
    periodoHasta: ventanaHasta,
    promedioResultante: Number((haberReajustado / divisorAplicado).toFixed(2)),
  }

  const projectedRows = valid.filter((r) => r.projected)
  const indexQualityStats = valid.reduce((acc, r) => {
    const baseMethod = String(r.indiceBaseMetodo || 'missing')
    const ceseMethod = String(r.indiceCeseMetodo || 'missing')
    const methods = [baseMethod, ceseMethod]
    methods.forEach((m) => {
      acc.total += 1
      if (m === 'exact' || m === 'normalized_exact' || m === 'interpolated') acc.robust += 1
      else acc.weak += 1
    })
    return acc
  }, { total: 0, robust: 0, weak: 0 })
  const weakPctIndices = indexQualityStats.total > 0
    ? Number(((indexQualityStats.weak / indexQualityStats.total) * 100).toFixed(2))
    : 0

  const firstRow = valid[0] || {}
  const traceability = {
    versionNormativa: normativeVersion,
    indiceVersion: String(firstRow.indiceVersion || options?.indiceVersion || 'N/D'),
    fechaCorteCalculo: String(firstRow.fechaCorteCalculo || options?.fechaCorteCalculo || 'N/D'),
  }

  const fundamentosPorLinea = valid
    .slice()
    .sort((a, b) => toYearMonth(a.periodoKey || a.periodo || a.periodoISO) - toYearMonth(b.periodoKey || b.periodo || b.periodoISO))
    .map((r) => {
      const periodo = r.periodoKey || r.periodo || r.periodoISO || '-'
      const indice = String(r.indiceAplicado || expediente?.fallo || 'ISBIC')
      const metodoBase = String(r.indiceBaseMetodo || 'missing')
      const metodoCese = String(r.indiceCeseMetodo || 'missing')
      const tasa = String(r.tasaInteresTipo || 'PASIVA_14290')
      const fuenteNormativa = String(r.fuenteNormativa || 'sin_tope')
      const projected = Boolean(r.projected)
      const estimated = Boolean(r.isEstimatedIndex)
      const tags = []
      if (fuenteNormativa === 'tope_base_imponible') tags.push('tope_art9_ley24463')
      if (projected) tags.push('proyeccion_tecnica')
      if (estimated) tags.push('indice_estimado')
      if (r.fueraPrescripcion) tags.push('fuera_prescripcion')
      const rationale = [
        `Índice ${indice} (${metodoBase}/${metodoCese})`,
        `Interés ${tasa}`,
        `Norma ${fuenteNormativa}`,
        tags.length ? `Tags ${tags.join(',')}` : '',
      ].filter(Boolean).join(' | ')
      return { periodo, rationale }
    })
  if (traceability.versionNormativa === 'N/D' || traceability.indiceVersion === 'N/D' || traceability.fechaCorteCalculo === 'N/D') {
    alertas.push('Trazabilidad incompleta: falta versión normativa, versión de índices o fecha de corte de cálculo.')
  }

  const proyeccion = {
    activa: projectedRows.length > 0,
    desde: projectedRows[0]?.periodoKey || '',
    hasta: projectedRows[projectedRows.length - 1]?.periodoKey || '',
    disclaimer: projectedRows.length > 0
      ? `ADVERTENCIA DE PROYECCIÓN: Los períodos ${projectedRows[0]?.periodoKey || ''} a ${projectedRows[projectedRows.length - 1]?.periodoKey || ''} no cuentan con respaldo en la sábana de aportes actual y se incluyen a fines de proyectar el haber inicial teórico al momento del cese efectivo.`
      : '',
  }

  const ceseDate = periodoToDate(expediente?.fechaCese)
  const hoyDate = new Date()
  const puenteMeses = ceseDate
    ? Math.abs((ceseDate.getFullYear() - hoyDate.getFullYear()) * 12 + (ceseDate.getMonth() - hoyDate.getMonth()))
    : 0
  const justificacionMovilidad = puenteMeses > 2
    ? `Se aplicó puente de movilidad por desfasaje temporal entre el cese y la fecha actual (${puenteMeses} meses), utilizando la escalera legal vigente para evitar anacronismos en la cuantificación.`
    : 'No se requirió puente de movilidad relevante por cercanía temporal entre cese y fecha de cálculo.'

  const aniosPorHijos = Number((hijos + adoptivos * 2 + discapacidad).toFixed(2))
  const aniosSubsanados475 = Number(bonusPorHijos.toFixed(2))
  const subsanacionDetalle = {
    aplicaDecreto475: aniosSubsanados475 > 0,
    aniosSumadosDecreto475: aniosSubsanados475,
    detalleAnios: {
      porHijos: aniosPorHijos,
      porAUH: expediente?.cobraAUH === 'si' ? 1 : 0,
    },
    texto: aniosSubsanados475 > 0
      ? `Se aplicó subsanación por Decreto 475/2021, incorporando ${aniosSubsanados475.toFixed(2)} años de servicios computables por tareas de cuidado.`
      : 'No se aplicó subsanación por Decreto 475/2021 al no verificarse años adicionales por tareas de cuidado.',
  }

  const advertenciaTopes = {
    aplicaArt9Ley24463: peor.quita > 0,
    doctrina: 'Fallo Gualtieri',
    porcentajeQuitaDetectado: Number(peor.quita.toFixed(2)),
    umbralAplicadoPct: Number(confiscatoriedadThresholdPct.toFixed(2)),
    texto: peor.quita > 0
      ? `Se detectó quita por topes (Art. 9 Ley 24.463) del ${peor.quita.toFixed(2)}% en su pico máximo, conforme pauta de control de confiscatoriedad (umbral ${confiscatoriedadThresholdPct.toFixed(2)}%).`
      : 'No se detectó quita por topes relevante en el tramo analizado.',
  }

  const cumplimientoNormativo = {
    articulo: 'Art. 24 Ley 24.241',
    remuneracionesRealesConsideradas: ventanaPromedio.length,
    divisorAplicado,
    seleccionArbitraria: false,
    texto: promedioFundamento,
  }

  const corteJul94 = 199407
  const scenarioKey = String(options?.activeRules?.caseMeta?.scenarioKey || '')
  const criterioDocenteMixto = options?.activeRules?.caseMeta?.criterioOtorgamientoDocenteMixto || null
  const isPensionScenario = scenarioKey === 'PENSION_DERIVADA' || scenarioKey === 'PENSION_DIRECTA'
  const pensionCalc = isPensionScenario
    ? calcularHaberPension(expediente, valid, options?.activeRules || {})
    : null
  const isDocenteScenario = scenarioKey === 'DOCENTE_PURO' ||
    (scenarioKey === 'DOCENTE_MIXTO' && Boolean(criterioDocenteMixto?.applyDocenteFormula))
  const mixedFormula = calcularHaberMixto(valid, {
    corteJul94,
    divisorPolicy,
  })
  const aniosPre94 = Number(((mixedFormula?.dependencia?.aniosPre94 || 0) + (mixedFormula?.autonomos?.aniosPre94 || 0)).toFixed(4))
  const aniosPost94 = Number(((mixedFormula?.dependencia?.aniosPost94 || 0) + (mixedFormula?.autonomos?.aniosPost94 || 0)).toFixed(4))
  const pc = Number(mixedFormula?.pcTotal || 0)
  const pap = Number(mixedFormula?.papTotal || 0)
  const pbuFecha = periodoToDate(expediente?.fechaCese) || new Date()
  const pbu = Number(obtenerPBUEnFecha(pbuFecha) || PBU_VIGENTE || 0)
  const docenteCalc = calcularHaberDocente(expediente, options?.activeRules || {}, valid)
  const movilidadCalc = isDocenteScenario
    ? {
      factor: getRipdocFactor(expediente?.fechaCese, options?.fechaCorteCalculo || null, seriesOficiales),
      tramoAplicado: 'Movilidad RIPDOC (marzo/septiembre)',
      referencia: 'MOVILIDAD_RIPDOC',
    }
    : buildMovilidadDesdeCese(
      expediente?.fechaCese,
      options?.fechaCorteCalculo || null,
      options?.indicesMovilidad || {},
    )
  const movilidadAcumulada = Number(movilidadCalc.factor || 1)
  const haberBasePrestacionalInicial = isPensionScenario
    ? Number(pensionCalc?.haberInicialPension || 0)
    : isDocenteScenario
    ? Number(docenteCalc?.componentesHaber?.haberInicialSustitutivo || 0)
    : (pbu + pc + pap)
  let haberBasePrestacional = haberBasePrestacionalInicial
  let formulaPath = isPensionScenario ? 'pension' : (isDocenteScenario ? 'docente' : 'general')
  let fallbackGeneralApplied = false

  const floorMinimoRazonable = Math.max(10000, Number(HABER_MINIMO_LEGAL || 0) * 0.1)
  const shouldFallbackSpecialScenario =
    (isPensionScenario || isDocenteScenario) &&
    valid.length > 0 &&
    haberBasePrestacional > 0 &&
    haberBasePrestacional < floorMinimoRazonable

  if (shouldFallbackSpecialScenario) {
    const generalBase = Number((pbu + pc + pap) || 0)
    if (generalBase > 0) {
      fallbackGeneralApplied = true
      formulaPath = 'general_fallback_from_special'
      haberBasePrestacional = generalBase
      alertas.push(
        `Guardarraíl activado: escenario especial con haber colapsado (< ${Math.round(floorMinimoRazonable)}). ` +
        'Se aplicó fallback automático a fórmula general para evitar error silencioso.',
      )
    }
  }

  const haberBolsilloReajustado = haberBasePrestacional * movilidadAcumulada
  const haberActualAnses = toNumber(expediente?.haberActual)
  const diferencialMensualBolsillo = Math.max(0, haberBolsilloReajustado - haberActualAnses)
  const retroRowsBase120 = ventanaPromedio
    .filter((r) => !r.invalidoPorCese)
    .filter((r) => Number(r.incluirEnPromedio) !== 0)
  const retroactivoBase120 = retroRowsBase120.reduce((acc, r) => acc + toNumber(r.acumuladoConInteres), 0)
  const retroactivoHistoricoAmplio = valid.reduce((acc, r) => acc + toNumber(r.acumuladoConInteres), 0)
  const retroactivoRaw = isBaseMode ? retroactivoHistoricoAmplio : retroactivoBase120
  const retroactivoTotal = diferencialMensualBolsillo > 0 ? retroactivoRaw : 0
  if (retroactivoRaw > 0 && diferencialMensualBolsillo <= 0) {
    alertas.push('Coherencia automática aplicada: se neutralizó retroactivo por no existir mejora mensual proyectada del haber inicial.')
  }
  if (!isBaseMode && retroactivoHistoricoAmplio > retroactivoBase120 * 1.5) {
    alertas.push('Se aplicó criterio conservador de retroactividad pericial: se privilegió base de 120 remuneraciones computables para evitar sobreestimación.')
  }
  if (weakPctIndices > 35) {
    alertas.push(`Calidad de índices insuficiente para estándar pericial estricto (${weakPctIndices}% de trazas con fallback fuerte).`)
  }
  const topeGlobalAudit = options?.topeGlobalAudit || null
  if (topeGlobalAudit?.inaplicablePorConfiscatoriedad) {
    alertas.push(`Tope máximo ANSES potencialmente confiscatorio: quita ${Number(topeGlobalAudit?.quitaPct || 0).toFixed(2)}%. Sugerir planteo de inaplicabilidad (${String(topeGlobalAudit?.doctrinaSugerida || 'Tual / Actis Caporale')}).`)
  }
  if (scenarioKey === 'DOCENTE_MIXTO' && criterioDocenteMixto?.explicacionJuridica) {
    alertas.push(`Docente mixto - criterio de otorgamiento: ${String(criterioDocenteMixto.explicacionJuridica)}`)
  }
  if (scenarioKey === 'PENSION_DIRECTA' && options?.activeRules?.caseMeta?.criterioRegularidad) {
    const reg = options.activeRules.caseMeta.criterioRegularidad
    alertas.push(
      `AUDITORIA DE REGULARIDAD (Dec. 460/99): ${Number(reg?.mesesAportados36 || 0)} meses aportados en los ultimos 36. ` +
      `Condicion: [${String(reg?.condicionLegal || 'N/D')}]. Tasa de Ingreso Base aplicada: ${Number((Number(reg?.factorQuita || 0) * 100)).toFixed(2)}%.`,
    )
  }

  const resumenEjecutivo = {
    haberActualAnses: Number(haberActualAnses.toFixed(2)),
    haberBolsilloReajustado: Number(haberBolsilloReajustado.toFixed(2)),
    diferencialMensual: Number(diferencialMensualBolsillo.toFixed(2)),
    incrementoMensualPct: haberActualAnses > 0 ? Number(((diferencialMensualBolsillo / haberActualAnses) * 100).toFixed(2)) : 0,
    retroactivoTotal: Number(retroactivoTotal.toFixed(2)),
    prestaciones: {
      pbu: Number(pbu.toFixed(2)),
      pc: Number((isDocenteScenario ? 0 : pc).toFixed(2)),
      pap: Number((isDocenteScenario ? 0 : pap).toFixed(2)),
      pension: isPensionScenario ? Number(pensionCalc?.haberInicialPension || 0) : 0,
      pensionTipo: isPensionScenario ? String(pensionCalc?.tipoPension || '') : '',
      pensionRegularidad: isPensionScenario ? String(pensionCalc?.categoriaAportante || '') : '',
      haberInicialDocente: isDocenteScenario ? Number(docenteCalc?.componentesHaber?.haberInicialSustitutivo || 0) : 0,
      regimenDocenteAplicado: isDocenteScenario,
        movilidadAcumulada: Number(movilidadAcumulada.toFixed(4)),
        movilidadTramoAplicado: movilidadCalc.tramoAplicado,
        movilidadReferencia: movilidadCalc.referencia,
        divisorPromedioAplicado: divisorAplicado,
      divisorPromedioPolicy: divisorPolicy,
      separacionHaberes: {
        mesesDependencia: ventanaDependencia.length,
        mesesAutonomos: ventanaAutonomos.length,
        promedioDependencia: Number(promedioDependencia.toFixed(2)),
        promedioAutonomos: Number(promedioAutonomos.toFixed(2)),
        pcDependencia: Number(mixedFormula?.dependencia?.pc || 0),
        papDependencia: Number(mixedFormula?.dependencia?.pap || 0),
        pcAutonomos: Number(mixedFormula?.autonomos?.pc || 0),
        papAutonomos: Number(mixedFormula?.autonomos?.pap || 0),
      },
    },
  }

  const diagnosticoCalculo = {
    scenarioKey,
    isPensionScenario,
    isDocenteScenario,
    formulaPath,
    fallbackGeneralApplied,
    floorMinimoRazonable: Number(floorMinimoRazonable.toFixed(2)),
    haberBasePrestacionalInicial: Number(haberBasePrestacionalInicial.toFixed(2)),
    haberBasePrestacionalFinal: Number(haberBasePrestacional.toFixed(2)),
    movilidadAcumulada: Number(movilidadAcumulada.toFixed(6)),
    haberBolsilloReajustado: Number(haberBolsilloReajustado.toFixed(2)),
    validRows: valid.length,
  }

  const totalIntereses = valid.reduce((acc, r) => acc + Math.max(0, toNumber(r.acumuladoConInteres) - toNumber(r.diferenciaMensual)), 0)
  const capitalNominal = valid.reduce((acc, r) => acc + toNumber(r.diferenciaMensual), 0)
  const totalDeuda = capitalNominal + totalIntereses

  const estructuraDictamen = {
    iObjeto: `Haber administrativo actual: ${Math.round(haberAnses).toLocaleString('es-AR')}. Haber reajustado: ${Math.round(haberReajustado).toLocaleString('es-AR')}.`,
    iiMetodologia: `Se aplica ${expediente?.fallo || 'ISBIC'} con doctrina Elliff/Blanco segun fecha de cese y puente de movilidad cuando la brecha temporal supera 2 meses.`,
    iiiLiquidacionAcreencias: {
      capitalNominal: Number(capitalNominal.toFixed(2)),
      interesesCom14290: Number(totalIntereses.toFixed(2)),
      totalDeudaFechaCorte: Number(totalDeuda.toFixed(2)),
      retroactivoBase120: Number(retroactivoBase120.toFixed(2)),
      retroactivoHistoricoAmplio: Number(retroactivoHistoricoAmplio.toFixed(2)),
    },
    ivConclusion: `Confiscatoriedad detectada: ${diferencial.toFixed(2)}%.`,
  }

  const fundamentosEstrategiaPericial = {
    titulo: 'Fundamentos de la Estrategia Pericial',
    resumen: [
      `Indice aplicado: ${expediente?.fallo || 'ISBIC'} segun fecha de cese y doctrina aplicable.`,
      justificacionMovilidad,
      subsanacionDetalle.texto,
      advertenciaTopes.texto,
      cumplimientoNormativo.texto,
    ],
    textoPlano: `Fundamentos de la Estrategia Pericial:\n- Indice aplicado: ${expediente?.fallo || 'ISBIC'} segun fecha de cese y doctrina aplicable.\n- ${justificacionMovilidad}\n- ${subsanacionDetalle.texto}\n- ${advertenciaTopes.texto}\n- ${cumplimientoNormativo.texto}`,
  }

  const integridad = {
    totalFilas: valid.length,
    factual: valid.filter((r) => String(r.estadoIdentidad || '') === 'FACTUAL').length,
    inferred: valid.filter((r) => String(r.estadoIdentidad || '') === 'INFERRED').length,
    corrupt: valid.filter((r) => String(r.estadoIdentidad || '') === 'CORRUPT').length,
    scoreGlobal: valid.length
      ? Number((valid.reduce((acc, r) => acc + Number(r.confianzaIdentidad || 30), 0) / valid.length).toFixed(2))
      : 0,
  }

  const puntosRechazoPotenciales = []
  if (valid.some((r) => r.invalidIndex)) puntosRechazoPotenciales.push('Faltan índices oficiales para uno o más períodos de la base de cálculo.')
  if (valid.some((r) => r.projected)) puntosRechazoPotenciales.push('Se detectaron periodos proyectados sin respaldo documental directo en sabana de aportes.')
  if (integridad.corrupt > 0) puntosRechazoPotenciales.push(`Existen ${integridad.corrupt} filas con identidad no factual (estado CORRUPT).`)
  if (valid.some((r) => r.requiereRevision || r.requiresValidation)) puntosRechazoPotenciales.push('Existen filas marcadas para revisión manual por legibilidad o consistencia.')
  if (valid.some((r) => r.prescripcionIndeterminada)) puntosRechazoPotenciales.push('No se informó fecha de primer reclamo administrativo; el alcance de prescripción requiere validación legal específica.')
  const edadAlCese = computeEdadEnFecha(expediente?.fechaNacimiento, expediente?.fechaCese)
  const edadMinima = sexo === 'F' ? 60 : 65
  if (edadAlCese > 0 && edadAlCese < edadMinima) {
    puntosRechazoPotenciales.push('RECHAZO AUTOMATICO: El titular no alcanza la edad minima jubilatoria (Art. 19 Ley 24.241).')
  }
  if (haberBolsilloReajustado < haberActualAnses) {
    puntosRechazoPotenciales.push('RECHAZO AUTOMATICO: No se detecta perjuicio economico (haber reajustado inferior al haber actual).')
  }

  const metodologiaSintetica = {
    pasos: [
      'Extracción OCR y mapeo estructurado por período/empleador/monto.',
      'Sanitización con política cero descarte y validación CUIT por Módulo 11.',
      'Resolución de identidad por razón social (Levenshtein) cuando CUIT es inválido o ausente.',
      'Consolidación por clave única período+CUIT y separación de simultaneidad real.',
      'Aplicación normativa (topes, índice, intereses) y selección de base legal de cálculo.',
      'Control de integridad (Shadow Billing) cuando existen totales de resumen en origen.',
    ],
  }

  const defensaJudicial = {
    tesis: 'El resultado pericial surge de un proceso reproducible, auditable y no arbitrario, con reglas fijas de selección, saneo y actualización.',
    puntosClave: [
      'Se aplicó Art. 24 Ley 24.241 sobre base de 120 remuneraciones computables.',
      'Toda corrección de identidad queda trazada (CUIT original, motivo de falla y CUIT recuperado).',
      'No se descartan aportes por baja calidad OCR; se marcan para revisión.',
      'Topes y quitas se explicitan con porcentaje y período de mayor impacto.',
      'La liquidación diferencia capital nominal e intereses para control jurisdiccional.',
    ],
  }

  const anexosTecnicos = {
    includeResumenEjecutivo: true,
    includeMetodologia: true,
    includeIntegridad: true,
    includeCalculoPrestacional: true,
  }

  const fechaAlta = String(expediente?.fechaOtorgamientoBeneficio || '')
  const fechaReclamoAdmin = String(expediente?.fechaReclamoAdministrativo || '')
  const fechaDenegatoriaODemanda = String(expediente?.fechaDenegatoriaODemanda || '')
  const prescripcionClinica = {
    fechaAlta,
    fechaReclamoAdministrativo: fechaReclamoAdmin,
    fechaDenegatoriaODemanda,
    limiteAdministrativo: fechaReclamoAdmin ? minusMonthsIso(fechaReclamoAdmin, 24) : '',
    limiteJudicial: fechaDenegatoriaODemanda ? minusMonthsIso(fechaDenegatoriaODemanda, 24) : '',
  }

  return {
    datosCausa: {
      cese: expediente?.fechaCese || 'N/A',
      fallo: expediente?.fallo || 'ISBIC',
    },
    estadisticas: {
      haberAnses: Math.round(haberAnses),
      haberReajustado: Math.round(haberReajustado),
      diferencial: `${diferencial.toFixed(1)}%`,
      divisorPromedio,
      mesesConTope: conTope.length,
      maximaQuita: `${peor.quita.toFixed(1)}% en periodo ${peor.periodo}`,
      calidadIndices: {
        robustos: indexQualityStats.robust,
        debiles: indexQualityStats.weak,
        porcentajeDebil: weakPctIndices,
      },
    },
    topRemuneracionesActualizadas,
    periodoComputoPromedio,
    computoTareasCuidado: {
      aplica: sexo === 'F' || sexo === 'X',
      mostrarSeccion: mostrarTareasCuidado,
      hijos,
      adoptivos,
      discapacidad,
      cobraAUH: expediente?.cobraAUH === 'si',
      aniosReconocidos: Number(bonusPorHijos.toFixed(2)),
      aporteAniosBase: Number(aporteAniosBase.toFixed(2)),
      aporteAniosTotal: Number(aporteAniosTotal.toFixed(2)),
      mesesFaltantesMoratoria,
      mensajeMoratoria: mesesFaltantesMoratoria > 0 ? `La parte actora debera regularizar ${mesesFaltantesMoratoria} meses de aportes para acceder al beneficio.` : 'No requiere regularizacion adicional por moratoria.',
      narrativaViabilidad,
      fundamentoLegal: 'Reconocimiento de servicios por tareas de cuidado conforme Decreto 475/2021.',
      notaCautelaMoratoria: 'Nota: El reajuste aqui planteado se enfoca en la recomposicion de los haberes derivados de aportes efectivos. Respecto a las Unidades de Pago de Deuda Previsional (Ley 27.705), se mantiene la reserva de ley segun la vigencia del plan de pagos.',
    },
    hojaDeTrabajo,
    proyeccion,
    justificacionMovilidad,
    subsanacionDetalle,
    advertenciaTopes,
    topeGlobalAudit,
    cumplimientoNormativo,
    traceability,
    fundamentosEstrategiaPericial,
    defensaJudicial,
    metodologiaSintetica,
    integridad,
    puntosRechazoPotenciales,
    anexosTecnicos,
    fundamentosPorLinea,
    consistenciaJuridica: options?.consistenciaJuridica || null,
    preEmisionAudit: options?.preEmisionAudit || null,
    prescripcionClinica,
    criterioOtorgamientoDocenteMixto: criterioDocenteMixto,
    estructuraDictamen,
    resumenEjecutivo,
    diagnosticoCalculo,
    alertas,
  }
}
